import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { onAuthStateChanged } from 'firebase/auth';
import {
  InvoiceDraft,
  InvoiceTemplate,
  InvoiceSettings,
  Customer,
  AuthUser,
  AuthSession,
  PasswordReset,
} from '../../types';
import {
  assertFirebaseAuth,
  getFirebaseAuth,
} from '../firebase';

export interface InvoiceDB extends DBSchema {
  invoices: {
    key: string;
    value: InvoiceDraft;
    indexes: { 'by-date': string; 'by-number': string };
  };
  template: {
    key: 'default';
    value: InvoiceTemplate;
  };
  settings: {
    key: 'default';
    value: InvoiceSettings;
  };
  customers: {
    key: string;
    value: Customer;
    indexes: { 'by-name': string; 'by-updated': string };
  };
  users: {
    key: string;
    value: AuthUser;
    indexes: { 'by-email': string };
  };
  sessions: {
    key: string;
    value: AuthSession;
    indexes: { 'by-user': string };
  };
  passwordResets: {
    key: string;
    value: PasswordReset;
    indexes: { 'by-user': string };
  };
}

const DB_NAME = 'huipsov-invoices';
const DB_VERSION = 4;

let dbPromise: Promise<IDBPDatabase<InvoiceDB>> | null = null;

function upgradeInvoiceDb(db: {
  objectStoreNames: DOMStringList;
  createObjectStore: IDBPDatabase<InvoiceDB>['createObjectStore'];
}, oldVersion: number) {
  if (oldVersion < 1) {
    const invoiceStore = db.createObjectStore('invoices', {
      keyPath: 'id',
    });
    invoiceStore.createIndex('by-date', 'updatedAt');
    invoiceStore.createIndex('by-number', 'number');
    db.createObjectStore('template');
    db.createObjectStore('settings');
  }

  if (oldVersion < 2) {
    if (!db.objectStoreNames.contains('users')) {
      const users = db.createObjectStore('users', { keyPath: 'id' });
      users.createIndex('by-email', 'email', { unique: true });
    }
    if (!db.objectStoreNames.contains('sessions')) {
      const sessions = db.createObjectStore('sessions', { keyPath: 'id' });
      sessions.createIndex('by-user', 'userId');
    }
    if (!db.objectStoreNames.contains('passwordResets')) {
      const resets = db.createObjectStore('passwordResets', {
        keyPath: 'id',
      });
      resets.createIndex('by-user', 'userId');
    }
  }

  // v3 + v4: ensure customers store exists (also recovers stale HMR connections)
  if (oldVersion < 4) {
    if (!db.objectStoreNames.contains('customers')) {
      const customers = db.createObjectStore('customers', {
        keyPath: 'id',
      });
      customers.createIndex('by-name', 'name');
      customers.createIndex('by-updated', 'updatedAt');
    }
  }
}

export async function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<InvoiceDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        upgradeInvoiceDb(db, oldVersion);
      },
    });
  }

  const db = await dbPromise;
  if (!db.objectStoreNames.contains('customers')) {
    db.close();
    dbPromise = openDB<InvoiceDB>(DB_NAME, DB_VERSION, {
      upgrade(d, oldVersion) {
        upgradeInvoiceDb(d, oldVersion);
      },
    });
    return dbPromise;
  }
  return db;
}

/** Wait until Firebase Auth has the expected signed-in user (token ready for Firestore). */
export async function ensureFirebaseUid(uid: string): Promise<void> {
  const auth = assertFirebaseAuth();
  if (auth.currentUser?.uid === uid) return;

  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      unsub();
      reject(
        new Error(
          'Firebase Auth is not ready. Refresh the page and try again.',
        ),
      );
    }, 10000);

    const unsub = onAuthStateChanged(auth, (user) => {
      if (user?.uid === uid) {
        window.clearTimeout(timeout);
        unsub();
        resolve();
      }
    });

    if (auth.currentUser?.uid === uid) {
      window.clearTimeout(timeout);
      unsub();
      resolve();
    }
  });
}

function currentUid(): string | null {
  return getFirebaseAuth()?.currentUser?.uid ?? null;
}

/**
 * Resolve storage owner.
 * Pass `uid` from AuthProvider when auth is ready so React session matches storage.
 * - string → Firestore only (never IndexedDB)
 * - null → IndexedDB only (guest / signed out)
 */
export function resolveUid(uid?: string | null): string | null {
  if (uid !== undefined) return uid;
  return currentUid();
}

/** Firestore rejects `undefined` field values — strip them recursively. */
export function toFirestoreData<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    return value.map((item) => toFirestoreData(item)) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[key] = toFirestoreData(v);
  }
  return out as T;
}
